import {
  HorizontalPositionTypes,
  Point,
  Renderer,
  VerticalPositionTypes,
} from "@may-i/mash-renderer/refactor";
import { FabricImage, XY } from "future-fabric";
import {
  CAMERA_ICON_CIRCLE_RADIUS,
  CAMERA_ICON_WIDTH,
  CROP_CANVAS_DEFAULT_HEIGHT,
  CROP_CANVAS_DEFAULT_WIDTH,
  DEFAULT_BACKGROUND_IMAGE_WIDTH,
  SLOT_LAYOUT_VALUES,
  SLOT_SIZE,
} from "~/_legacy/constants/shape";
import { Camera } from "~/_legacy/types/analysis";
import { DataForPointConversion, SlotPoint } from "~/_legacy/types/shape";
import { findMaxCount } from "./algorithm";

export const adjustCameraShapePoint = (point: Point, iconScale: number) => {
  const dist = (CAMERA_ICON_WIDTH / 2 + CAMERA_ICON_CIRCLE_RADIUS) * iconScale;

  return new Point({
    x: point.x + dist,
    y: point.y + dist,
  });
};

export const arePointsEqual = (points1: Point[], points2: Point[]): boolean => {
  if (points1.length !== points2.length) return false;

  return points1.every((p1) =>
    points2.some((p2) => Math.abs(p1.x - p2.x) < 0.0001 && Math.abs(p1.y - p2.y) < 0.0001)
  );
};

export const convertToRelativePoint = (
  from: Point,
  dataForPointConversion: DataForPointConversion
) => {
  const {
    rendererViewportSize,
    rendererViewportSizeScale,
    backgroundImageSize,
    backgroundImageScaledSize,
  } = dataForPointConversion;

  const offset = {
    x: (rendererViewportSize.width - backgroundImageScaledSize.width) / 2,
    y: (rendererViewportSize.height - backgroundImageScaledSize.height) / 2,
  };
  const scale = rendererViewportSizeScale;

  return new Point({
    x: (from.x / 100) * backgroundImageSize.width * scale + offset.x,
    y: (from.y / 100) * backgroundImageSize.height * scale + offset.y,
  });
};

export const convertToAbsolutePoint = (
  from: Point,
  dataForPointConversion: DataForPointConversion
) => {
  const {
    rendererViewportSize,
    rendererViewportSizeScale,
    backgroundImageSize,
    backgroundImageScaledSize,
  } = dataForPointConversion;

  const offset = {
    x: (rendererViewportSize.width - backgroundImageScaledSize.width) / 2,
    y: (rendererViewportSize.height - backgroundImageScaledSize.height) / 2,
  };
  const scale = rendererViewportSizeScale;

  // 좌표 객체를 반환 (Point 객체가 아님)
  return {
    x: ((from.x - offset.x) / (scale * backgroundImageSize.width)) * 100,
    y: ((from.y - offset.y) / (scale * backgroundImageSize.height)) * 100,
  };
};

export const createCountingLineEventTypeMap = (
  coordinates: [Point, Point],
  eventTypeMap: { left: string; right: string }
) => {
  const initialEventTypeMap = eventTypeMap;

  const vec = new Point(coordinates[1]).minus(new Point(coordinates[0]));

  if (vec.y < 0 || (vec.y === 0 && vec.x > 0)) {
    return initialEventTypeMap;
  } else {
    return {
      left: initialEventTypeMap.right,
      right: initialEventTypeMap.left,
    };
  }
};

export const getBoundedPoints = (coordinates: XY[]) => {
  const minX = 0;
  const minY = 0;
  const maxX = 100;
  const maxY = 100;

  const minPointX = Math.min(...coordinates.map((point) => point.x));
  const minPointY = Math.min(...coordinates.map((point) => point.y));
  const maxPointX = Math.max(...coordinates.map((point) => point.x));
  const maxPointY = Math.max(...coordinates.map((point) => point.y));

  const moveX = minPointX < minX ? minX - minPointX : maxPointX > maxX ? maxX - maxPointX : 0;
  const moveY = minPointY < minY ? minY - minPointY : maxPointY > maxY ? maxY - maxPointY : 0;

  return coordinates.map((point) => {
    return {
      x: point.x + moveX,
      y: point.y + moveY,
    };
  });
};

export const getCameraType = (camera: Camera) => {
  if (camera.adjustmentConfig) {
    return "edit";
  } else if (camera.additionConfig) {
    return "add";
  } else {
    return "default";
  }
};

export const getContainedImageSize = (fabricImage: FabricImage) => {
  const { width, height } = fabricImage;

  const ratio = width / height;

  if (ratio > CROP_CANVAS_DEFAULT_WIDTH / CROP_CANVAS_DEFAULT_HEIGHT) {
    return {
      width: CROP_CANVAS_DEFAULT_WIDTH,
      height: CROP_CANVAS_DEFAULT_WIDTH / ratio,
    };
  } else {
    return {
      width: CROP_CANVAS_DEFAULT_HEIGHT * ratio,
      height: CROP_CANVAS_DEFAULT_HEIGHT,
    };
  }
};

export const getSlotPoints = (renderer: Renderer) => {
  const { width: rendererWidth, height: rendererHeight } = renderer;
  const { width: slotWidth, height: slotHeight } = SLOT_SIZE;
  const { paddingLeft, paddingRight, paddingTop, paddingBottom, minGap } = SLOT_LAYOUT_VALUES;

  const horizontalCount = findMaxCount(
    rendererWidth - paddingLeft - paddingRight,
    slotWidth,
    minGap
  );
  const verticalCount = findMaxCount(
    rendererHeight - paddingTop - paddingBottom,
    slotHeight,
    minGap
  );

  const horizontalGap =
    (rendererWidth - (paddingLeft + paddingRight) - slotWidth * horizontalCount) /
    (horizontalCount - 1);
  const verticalGap =
    (rendererHeight - (paddingTop + paddingBottom) - slotHeight * verticalCount) /
    (verticalCount - 1);

  if (horizontalCount === 0 || verticalCount === 0) {
    return [];
  }

  const result: SlotPoint[] = [];

  const startPoint = new Point({
    x: paddingLeft,
    y: paddingTop,
  });

  let slotOrder = 0;
  for (let i = 0; i < horizontalCount; i += 1) {
    for (let j = 0; j < verticalCount; j += 1) {
      if (i === 0 || j === 0 || j === verticalCount - 1) {
        const point = new Point({
          x: startPoint.x + i * (slotWidth + horizontalGap),
          y: startPoint.y + j * (slotHeight + verticalGap),
        });

        const connectingPoint = point.clone();
        const connectingPointType: [HorizontalPositionTypes, VerticalPositionTypes] = [
          "center",
          "center",
        ];

        if (i === 0) {
          connectingPointType[0] = "right";
          connectingPoint.move({ dx: slotWidth });
        } else {
          connectingPoint.move({ dx: slotWidth / 2 });
        }

        if (j === 0) {
          connectingPointType[1] = "bottom";
          connectingPoint.move({ dy: slotHeight });
        } else if (j === verticalCount - 1) {
          connectingPointType[1] = "top";
        } else {
          connectingPoint.move({ dy: slotHeight / 2 });
        }

        result.push({ point: point, connectingPoint, connectingPointType, slotOrder });
        slotOrder += 1;
      }
    }
  }

  return result;
};

export const getNormalizedPoint = (point: XY) => {
  return {
    x: Math.min(100, Math.max(point.x, 0)),
    y: Math.min(100, Math.max(point.y, 0)),
  };
};

export const resizeCameraIconScale = (cameraIconScale: number, backgroundImageWidth: number) => {
  return (cameraIconScale * backgroundImageWidth) / DEFAULT_BACKGROUND_IMAGE_WIDTH;
};
